import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import { useAuth } from '@/context/AuthContext';
import { COURSES } from '@/data/courses';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import SEO from '@/components/layout/SEO';

function humanizeSlug(slug: string) {
    return slug
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getModuleState(status: string | undefined, index: number) {
    if (status === 'completed') return 'complete';
    if (status === 'in_progress' || index === 0) return 'active';
    return 'locked';
}

export default function CoursePage() {
    const { id } = useParams();
    const { progress, loading, badges, stats } = useGame();
    const { user } = useAuth();

    const course = COURSES[id || ''];

    if (!course) {
        return (
            <div className="relative overflow-hidden bg-[#0e0e0e] px-4 pb-16 pt-24 text-white md:px-6">
                <SEO title="Course not found" />
                <div className="mx-auto max-w-3xl border border-[#484848]/15 bg-[#131313] p-8 md:p-10">
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff716c]">
                        COURSE_NOT_FOUND
                    </p>
                    <h1 className="max-w-2xl font-['Space_Grotesk'] text-4xl font-black uppercase leading-tight tracking-tight md:text-6xl">
                        The route exists, but the course record does not.
                    </h1>
                    <p className="mt-4 max-w-xl font-['Inter'] text-sm leading-relaxed text-[#acabaa]">
                        Re-route back to the question map to pick a valid path.
                    </p>
                    <Link
                        to="/courses"
                        className="mt-8 inline-flex items-center justify-center bg-gradient-to-r from-[#8cacff] to-[#769dff] px-6 py-3 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.18em] text-[#001f55]"
                    >
                        RETURN TO QUESTION MAP
                    </Link>
                </div>
            </div>
        );
    }

    const loadingState = Boolean(user && loading);
    const completedModules = course.modules.filter((module) => progress[module.slug] === 'completed').length;
    const activeModule =
        course.modules.find((module, index) => getModuleState(progress[module.slug], index) !== 'complete') ??
        course.modules[0];
    const progressPct = course.modules.length > 0 ? Math.round((completedModules / course.modules.length) * 100) : 0;
    const handle = stats?.handle ?? user?.provider_id ?? 'PLAYER';
    const currentBadge = course.badge_on_complete ?? 'root-access';
    const recentBadge = badges[badges.length - 1];

    const courseSchema = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.title,
        description: course.description,
        provider: {
            '@type': 'Organization',
            name: 'startzone.dev',
            sameAs: 'https://startzone.dev',
        },
    });

    return (
        <div className="relative overflow-hidden bg-[#0e0e0e] text-white">
            <SEO
                title={`${course.title} — startzone.dev`}
                description={course.description}
                schema={courseSchema}
                canonicalUrl={`https://startzone.dev/courses/${id}`}
            />

            <div
                className="pointer-events-none absolute inset-0 opacity-[0.18]"
                style={{
                    background: 'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.05) 50%)',
                    backgroundSize: '100% 4px',
                }}
            />
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.08]"
                style={{
                    backgroundImage: 'radial-gradient(#8cacff 0.5px, transparent 0.5px)',
                    backgroundSize: '24px 24px',
                }}
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.04]">
                <span className="select-none font-['Space_Grotesk'] text-[18rem] font-black italic tracking-[-0.08em] md:text-[28rem]">
                    SZ
                </span>
            </div>
            <div className="pointer-events-none absolute top-24 left-12 hidden select-none font-mono text-[10px] uppercase leading-relaxed tracking-widest text-[#484848] lg:block">
                // COURSE_ID: {id?.toUpperCase() ?? 'UNKNOWN'}
                <br />
                // STATUS: {loadingState ? 'SYNCING' : 'STABLE'}
                <br />
                // XP_TARGET: {course.xp_reward.toLocaleString()} XP
            </div>

            <main className="relative mx-auto max-w-[96rem] px-4 pb-24 pt-6 md:px-6 md:pt-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
                    <aside className="space-y-6 xl:min-h-[calc(100vh-9rem)] xl:border-r xl:border-[#484848]/15 xl:pr-6">
                        <div className="border-l-2 border-[#8cacff] bg-[#131313] p-4">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#8cacff]">
                                        SYSTEM_STATUS
                                    </span>
                                    <span className="mt-1 block font-['Space_Grotesk'] text-2xl font-black uppercase tracking-tight text-white">
                                        LEVEL {stats?.level ?? 1}
                                    </span>
                                    <span className="block font-mono text-[10px] uppercase text-[#acabaa]">
                                        ELITE DEPLOYER
                                    </span>
                                </div>
                                <div className="flex h-16 w-16 items-center justify-center border border-[#484848]/20 bg-[#1f2020]">
                                    <MaterialIcon icon="terminal" size={26} className="text-[#00cffc]" />
                                </div>
                            </div>

                            <Link
                                to="/dashboard"
                                className="mt-4 inline-flex w-full items-center justify-center bg-[#1f2020] px-4 py-3 text-center font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.24em] text-[#8cacff] transition-colors hover:bg-[#252626]"
                            >
                                RETURN TO BASE CAMP
                            </Link>
                        </div>

                        <div className="overflow-hidden bg-[#131313]">
                            <div className="border-b border-[#484848]/15 px-4 py-4">
                                <div className="flex items-center justify-between">
                                    <span className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.24em] text-[#8cacff]">
                                        MODULE_SEQUENCE
                                    </span>
                                    <span className="font-mono text-[10px] uppercase text-[#acabaa]">
                                        {completedModules}/{course.modules.length}
                                    </span>
                                </div>
                                <div className="mt-3 h-1 bg-[#252626]">
                                    <div
                                        className="h-full bg-[#00cffc] shadow-[0_0_20px_rgba(0,207,252,0.15)]"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-2 p-4">
                                {course.modules.map((module, index) => {
                                    const state = getModuleState(progress[module.slug], index);
                                    const isActive = state === 'active';
                                    const isComplete = state === 'complete';
                                    const nextUnlock = module.unlock_condition ? humanizeSlug(module.unlock_condition) : null;

                                    return (
                                        <Link
                                            key={module.slug}
                                            to={`/courses/${id}/${module.slug}`}
                                            aria-label={`Module ${index + 1} ${module.title}`}
                                            className={`border-l-2 bg-[#191a1a] p-4 transition-colors hover:bg-[#1f2020] ${
                                                isComplete
                                                    ? 'border-[#00cffc]'
                                                    : isActive
                                                        ? 'border-[#8cacff]'
                                                        : 'border-[#484848]'
                                            } ${state === 'locked' ? 'opacity-60' : ''}`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0">
                                                    <span
                                                        className={`mb-1 block font-mono text-[10px] uppercase tracking-[0.18em] ${
                                                            isComplete
                                                                ? 'text-[#00cffc]'
                                                                : isActive
                                                                    ? 'text-[#8cacff]'
                                                                    : 'text-[#484848]'
                                                        }`}
                                                    >
                                                        Module {index + 1} // {state.toUpperCase()}
                                                    </span>
                                                    <h2 className="truncate font-['Space_Grotesk'] text-sm font-black uppercase leading-tight text-white">
                                                        {module.title}
                                                    </h2>
                                                </div>
                                                <MaterialIcon
                                                    icon={state === 'complete' ? 'task_alt' : state === 'active' ? 'bolt' : 'lock'}
                                                    size={16}
                                                    className={isComplete ? 'text-[#00cffc]' : isActive ? 'text-[#8cacff]' : 'text-[#484848]'}
                                                />
                                            </div>

                                            <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase text-[#acabaa]">
                                                <span>XP: {module.xp_reward}</span>
                                                <span>{module.is_boss ? 'BOSS' : 'NODE'}</span>
                                            </div>

                                            <div className="mt-2 h-1 bg-[#252626]">
                                                <div
                                                    className={`h-full ${
                                                        isComplete
                                                            ? 'bg-[#00cffc]'
                                                            : isActive
                                                                ? 'bg-[#8cacff]'
                                                                : 'bg-[#484848]'
                                                    }`}
                                                    style={{ width: isComplete ? '100%' : isActive ? '65%' : '12%' }}
                                                />
                                            </div>

                                            {nextUnlock ? (
                                                <p className="mt-2 truncate font-mono text-[9px] uppercase text-[#484848]">
                                                    Unlocks after {nextUnlock}
                                                </p>
                                            ) : null}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </aside>

                    <div className="space-y-6">
                        <header className="grid grid-cols-1 gap-6 bg-[#131313] p-4 md:grid-cols-[minmax(0,1.5fr)_18rem] md:p-6">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-4xl">{course.icon}</span>
                                    <span className="bg-[#1f2020] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8cacff]">
                                        COURSE — {id?.toUpperCase()}-01
                                    </span>
                                </div>
                                <h1 className="max-w-4xl font-['Space_Grotesk'] text-4xl font-black uppercase tracking-tighter text-white md:text-6xl">
                                    {course.title}
                                </h1>
                                <p className="max-w-3xl font-['Inter'] text-sm leading-relaxed text-[#acabaa] md:text-base">
                                    {course.description}
                                </p>
                                <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase">
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        MODULES: <span className="text-[#00cffc]">{course.modules.length}</span>
                                    </div>
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        BADGES: <span className="text-[#d674ff]">{badges.length}</span>
                                    </div>
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        XP: <span className="text-[#8cacff]">{course.xp_reward.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="border border-[#484848]/15 bg-[#1f2020] p-4">
                                <div className="mb-4 flex items-center justify-between">
                                    <span className="font-['Space_Grotesk'] text-xs font-black uppercase tracking-widest text-[#8cacff]">
                                        Progress
                                    </span>
                                    <span className="font-['Space_Grotesk'] text-2xl font-black">{loadingState ? '--' : `${progressPct}%`}</span>
                                </div>
                                <div className="mb-4 h-2 overflow-hidden bg-[#252626]">
                                    <div
                                        className="h-full bg-gradient-to-r from-[#8cacff] to-[#769dff]"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                                <div className="flex items-center justify-between font-mono text-[10px] uppercase text-[#acabaa]">
                                    <span>{loadingState ? 'Loading modules…' : `${completedModules} / ${course.modules.length} MODULES`}</span>
                                    <span>RANK: {progressPct === 100 ? 'MASTER' : 'NOVICE'}</span>
                                </div>
                            </div>
                        </header>

                        <div className="grid min-h-0 grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)_16rem]">
                            <div className="flex min-h-0 flex-col gap-4">
                                <div className="bg-[#131313] p-4">
                                    <div className="mb-4 flex items-center justify-between">
                                        <span className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.24em] text-[#8cacff]">
                                            Objective Chain
                                        </span>
                                        <MaterialIcon icon="route" size={16} className="text-[#00cffc]" />
                                    </div>
                                    <div className="space-y-3">
                                        {course.modules.slice(0, 3).map((module, index) => {
                                            const state = getModuleState(progress[module.slug], index);
                                            return (
                                                <div
                                                    key={module.slug}
                                                    className={`border-l-2 px-3 py-3 ${
                                                        state === 'complete'
                                                            ? 'border-[#00cffc] bg-[#1f2020]'
                                                            : state === 'active'
                                                                ? 'border-[#8cacff] bg-[#191a1a]'
                                                                : 'border-[#484848] bg-[#191a1a] opacity-70'
                                                    }`}
                                                >
                                                    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#acabaa]">
                                                        {String(index + 1).padStart(2, '0')} / {module.slug}
                                                    </span>
                                                    <h3 className="mt-1 font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                                        {module.title}
                                                    </h3>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="bg-[#131313] p-4">
                                    <span className="mb-3 block font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.24em] text-[#acabaa]">
                                        Mission Brief
                                    </span>
                                    <p className="font-['Inter'] text-sm leading-relaxed text-[#acabaa]">
                                        {course.modules.length > 0
                                            ? course.modules[0].content.quest
                                            : 'No mission briefing is available for this course.'}
                                    </p>
                                </div>
                            </div>

                            <div className="relative flex min-h-[28rem] items-center justify-center px-4 py-8 md:px-10">
                                <div className="absolute inset-0 rounded-full bg-[#8cacff]/5 blur-[120px]" />
                                <div className="relative flex aspect-square w-full max-w-[25rem] flex-col items-center justify-center border border-[#8cacff]/10 bg-[#0e0e0e]/40 p-8 text-center backdrop-blur-sm">
                                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 border border-[#8cacff]/20 bg-[#0e0e0e] px-4 py-1 font-mono text-[10px] uppercase tracking-widest text-[#8cacff]">
                                        SESSION_CONTINUE
                                    </div>
                                    <div className="absolute left-0 top-1/2 h-px w-4 -translate-x-1/2 bg-[#8cacff]" />
                                    <div className="absolute right-0 top-1/2 h-px w-4 translate-x-1/2 bg-[#8cacff]" />
                                    <span className="mb-4 font-['Space_Grotesk'] text-xs uppercase tracking-[0.3em] text-[#acabaa]">
                                        Current Objective
                                    </span>
                                    <h2 className="font-['Space_Grotesk'] text-[2.15rem] font-black uppercase leading-none tracking-tight text-white md:text-[2.75rem]">
                                        {activeModule.title.split(' ').slice(0, 2).join(' ')}
                                        <br />
                                        <span className="italic text-[#8cacff]">
                                            {activeModule.title.split(' ').slice(2).join(' ') || activeModule.slug.toUpperCase()}
                                        </span>
                                    </h2>
                                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8cacff]/70">
                                        {course.title}
                                    </p>

                                    <Link to={`/courses/${id}/${activeModule.slug}`} className="group relative mt-8 overflow-hidden px-10 py-4">
                                        <div className="absolute inset-0 translate-y-[2px] bg-gradient-to-r from-[#8cacff] to-[#769dff] transition-transform group-active:translate-y-0" />
                                        <div className="relative flex items-center gap-3 bg-[#0e0e0e] px-8 py-3">
                                            <MaterialIcon
                                                icon="play_arrow"
                                                size={18}
                                                className="text-[#8cacff] transition-transform group-hover:scale-110"
                                            />
                                            <span className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-[0.14em] text-[#8cacff]">
                                                RE-ENGAGE
                                            </span>
                                        </div>
                                    </Link>

                                    <div className="mt-8 flex flex-col items-center">
                                        <span className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00cffc] brightness-150">
                                            +{course.xp_reward} XP POTENTIAL
                                        </span>
                                        <div className="flex gap-1">
                                            <div className="h-1 w-1 bg-[#00cffc]" />
                                            <div className="h-1 w-1 bg-[#00cffc]" />
                                            <div className="h-1 w-1 bg-[#00cffc]" />
                                            <div className="h-1 w-1 bg-[#252626]" />
                                            <div className="h-1 w-1 bg-[#252626]" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex min-h-0 flex-col gap-6">
                                <div className="flex-1 overflow-hidden border-t-2 border-[#484848]/20 bg-[#131313] p-4 font-mono text-[10px] text-[#acabaa]">
                                    <div className="mb-4 flex items-center justify-between text-[#8cacff]/80">
                                        <span className="font-bold uppercase">System Log</span>
                                        <MaterialIcon icon="reorder" size={14} className="text-current" />
                                    </div>
                                    <div className="space-y-2 opacity-60">
                                        <p>[09:14:02] Session initialized...</p>
                                        <p>
                                            [09:14:05] Kernel status: <span className="text-[#00cffc]">STABLE</span>
                                        </p>
                                        <p>[09:16:30] Completed: {course.title}</p>
                                        <p>
                                            [09:16:31] <span className="text-[#8cacff]">+{course.xp_reward} XP Earned</span>
                                        </p>
                                        <p>[10:02:11] User connected via Terminal-HUD</p>
                                        <p className="animate-pulse">_</p>
                                    </div>
                                </div>

                                <div className="bg-[#1f2020] p-4">
                                    <span className="mb-3 block font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-widest text-[#acabaa]">
                                        Recent Unlocks
                                    </span>
                                    <div className="flex gap-4">
                                        <div className="flex h-12 w-12 items-center justify-center border border-[#d674ff]/30 bg-[#d674ff]/10">
                                            <MaterialIcon icon="military_tech" size={20} className="text-[#d674ff]" />
                                        </div>
                                        <div className="flex min-w-0 flex-col justify-center">
                                            <span className="truncate font-['Space_Grotesk'] text-xs font-black uppercase text-white">
                                                {loadingState ? 'Syncing course data' : recentBadge ?? currentBadge}
                                            </span>
                                            <span className="text-[9px] text-[#acabaa]">
                                                {loadingState ? 'Loading...' : `${course.modules.length} modules staged`}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="border border-[#8cacff]/5 bg-[#131313] p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                        <span className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.18em] text-[#acabaa]">
                                            Operator Handle
                                        </span>
                                        <span className="font-mono text-[10px] uppercase text-[#8cacff]">{handle}</span>
                                    </div>
                                    <div className="h-1 bg-[#252626]">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#8cacff] to-[#00cffc]"
                                            style={{ width: `${progressPct}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
